<template>
	<section class="no-marginbottom">
		<el-row>
			<el-col :sm="12" :xs="24">
				<h2>Externe Links</h2>
			</el-col>
			<el-col :sm="12" :xs="24" class="flex flex--justifyend" v-if="showAddButton">
				<TheButton @buttonclick="openAddDialog">
					<el-icon><Plus /></el-icon>Link hinzufügen
				</TheButton>
			</el-col>
		</el-row>
	</section>

	<LinkSlider v-if="links.length > 0" :links="links" @removeFromLinks="removeFromLinks" :allowDeleteFromServer="allowDeleteFromServer" />
	<!-- needed for form validation -->
	<el-form-item label="" prop="links" class="is-hidden">
		<el-input v-model="links" />
	</el-form-item>

	<el-dialog v-model="openDialog" :title="'Link hinzufügen'" draggable>
		<TheForm :model="request" :rules="rules" :id="id" ref="formRef" :waiting="waiting" :submitFunc="() => saveLink()">
			<el-form-item label="Link Titel" prop="name">
				<el-input v-model="request.name" />
			</el-form-item>
			<el-form-item label="Hyper-Link" prop="url">
				<el-input v-model="request.url" />
			</el-form-item>
			<el-form-item label="Typ" prop="type">
				<el-select v-model="request.type">
					<el-option v-for="item in linkTypeOptions" :key="item.value" :label="item.label" :value="item" />
				</el-select>
			</el-form-item>
		</TheForm>

		<template #footer>
			<span class="info--upload">
				Hinweis: Die Links werden hochgeladen, sobald Sie
				<template v-if="pageType === 'beschwerde'">die Beschwerde </template>
				<template v-if="pageType === 'fall'">den Fall </template>
				<template v-if="pageType === 'rueckfrage'">die Rückfrage </template>
				<template v-if="pageType === 'stellungnahme'">die Stellungnahme </template>
				speichern.
			</span>
			<div class="button-panel">
				<TheButton type="danger" @buttonclick="saveLink">Link speichern</TheButton>
				<TheButton type="default" @buttonclick="openDialog = false">Abbrechen</TheButton>
			</div>
		</template>
	</el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, PropType } from 'vue';
import TheButton from '../../components/TheButton.vue';
import TheForm from '../../components/TheForm.vue';
import LinkSlider from '../../components/slider/LinkSlider.vue';
import { Plus } from '@element-plus/icons-vue';
import { texts } from '../../utils/texts';
import { DetailLink, LinkDto, LinkResponse, LinkType } from '../../types/types';
import type { FormInstance } from 'element-plus';
import { linkTypeOptions, returnFormOptions } from '@/utils/formOptions';
import { validateForm, resetForm } from '@/utils/functions';

export default defineComponent({
	name: 'DetailsLinks',
	data() {
		return {
			id: 'linkForm',
			openDialog: false,
			waiting: false,
			request: {
				name: null,
				url: null,
				type: null,
			} as LinkDto | any,
			rules: {
				name: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' }],
				url: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' },
					{ min: 3, message: texts.formErrors.minChars, trigger: 'blur' },
				],
				type: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'change' }],
			},
		};
	},
	setup() {
		const formRef = ref<FormInstance>();
		return {
			formRef,
		};
	},
	props: {
		links: {
			type: Array as PropType<DetailLink[]>,
			default: () => [],
		},
		showAddButton: {
			type: Boolean,
			default: true,
		},
		openDialogFromOutside: {
			type: Boolean,
			default: false,
		},
		pageType: {
			type: String,
			default: '',
			required: true,
		},
		allowDeleteFromServer: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		texts() {
			return texts;
		},
		linkTypeOptions() {
			return returnFormOptions('linkType');
		},
	},
	watch: {
		openDialogFromOutside: {
			deep: true,
			handler(newVal) {
				this.openTheDialog();
			},
		},
	},
	emits: ['addToLinks', 'removeFromLinks'],
	components: {
		TheButton,
		Plus,
		LinkSlider,
		TheForm,
	},
	methods: {
		openTheDialog() {
			this.openDialog = true;
		},
		async saveLink() {
			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid) return;

			this.waiting = true;
			this.request.type = this.request.type.value;

			const linkObj = {
				link: { ...this.request },
				isNew: true,
			};

			this.$emit('addToLinks', linkObj);

			this.waiting = false;
			this.openDialog = false;
			resetForm(this.formRef, this.id);
		},
		openAddDialog() {
			this.resetRequest();
			this.openDialog = true;
		},
		resetRequest() {
			this.request = {
				name: null,
				url: null,
				type: null,
			};
		},
		removeFromLinks(link: DetailLink) {
			this.$emit('removeFromLinks', link);
		},
	},
});
</script>

<style lang="scss" scoped>
@use '../../scss/vars.scss' as *;

/* mobile styles */
@media screen and (max-width: $mobile_maxwidth) {
	section {
		.el-row {
			flex-direction: column;
			align-items: flex-start;
		}
	}
}
</style>
